import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  doc,
  onSnapshot,
  orderBy,
  query,
  updateDoc,
} from 'firebase/firestore';
import { db } from './firebase';
import { ProjectChat, ProjectChatMessage } from '../types';

interface FirestoreChatMessage {
  id?: string;
  role?: ProjectChatMessage['role'];
  content?: string;
  createdAt?: Timestamp | Date | null;
}

function toDate(value: Timestamp | Date | null | undefined): Date {
  if (value instanceof Timestamp) return value.toDate();
  if (value instanceof Date) return value;
  return new Date();
}

function serializeMessages(messages: ProjectChatMessage[]) {
  return messages.map((message) => ({
    id: message.id,
    role: message.role,
    content: message.content,
    createdAt: Timestamp.fromDate(message.createdAt),
  }));
}

function deserializeMessages(messages: unknown): ProjectChatMessage[] {
  if (!Array.isArray(messages)) return [];

  return messages
    .filter((message): message is FirestoreChatMessage => typeof message === 'object' && message !== null)
    .map((message, index): ProjectChatMessage => ({
      id: typeof message.id === 'string' && message.id.trim()
        ? message.id
        : `message-${index + 1}`,
      role: message.role === 'assistant' ? 'assistant' : 'user',
      content: typeof message.content === 'string' ? message.content : '',
      createdAt: toDate(message.createdAt),
    }))
    .filter((message) => message.content.trim().length > 0);
}

function deserializeChat(snapshot: { id: string; data: () => Record<string, unknown> }): ProjectChat {
  const data = snapshot.data();

  return {
    id: snapshot.id,
    title: typeof data.title === 'string' && data.title.trim() ? data.title : 'Untitled chat',
    messages: deserializeMessages(data.messages),
    createdAt: toDate((data.createdAt as Timestamp | Date | null | undefined)),
    updatedAt: toDate((data.updatedAt as Timestamp | Date | null | undefined)),
  };
}

export function subscribeToProjectChats(
  projectId: string,
  onChange: (chats: ProjectChat[]) => void
): () => void {
  const chatsQuery = query(
    collection(db, 'projects', projectId, 'chats'),
    orderBy('updatedAt', 'desc')
  );

  return onSnapshot(chatsQuery, (snapshot) => {
    onChange(snapshot.docs.map((docSnapshot) => deserializeChat(docSnapshot)));
  });
}

export async function createProjectChat(
  projectId: string,
  title: string,
  messages: ProjectChatMessage[]
): Promise<string> {
  const now = Timestamp.now();
  const docRef = await addDoc(collection(db, 'projects', projectId, 'chats'), {
    title,
    messages: serializeMessages(messages),
    createdAt: now,
    updatedAt: now,
  });

  await updateDoc(doc(db, 'projects', projectId), {
    updatedAt: now,
  });

  return docRef.id;
}

export async function updateProjectChat(
  projectId: string,
  chatId: string,
  updates: Partial<Pick<ProjectChat, 'title' | 'messages'>>
): Promise<void> {
  const now = Timestamp.now();
  const payload: {
    updatedAt: Timestamp;
    title?: string;
    messages?: ReturnType<typeof serializeMessages>;
  } = {
    updatedAt: now,
  };

  if (typeof updates.title === 'string') {
    payload.title = updates.title;
  }

  if (updates.messages !== undefined) {
    payload.messages = serializeMessages(updates.messages);
  }

  await updateDoc(doc(db, 'projects', projectId, 'chats', chatId), payload);
  await updateDoc(doc(db, 'projects', projectId), {
    updatedAt: now,
  });
}

export async function deleteProjectChat(
  projectId: string,
  chatId: string
): Promise<void> {
  const now = Timestamp.now();

  await deleteDoc(doc(db, 'projects', projectId, 'chats', chatId));
  await updateDoc(doc(db, 'projects', projectId), {
    updatedAt: now,
  });
}
